/**
 * Job share helpers — the candidate-facing URL for a job, source-tagged
 * variants of it, and the per-platform share messages.
 *
 * Every link handed out carries `?src=<channel>` so an application can be
 * traced back to the channel that produced it (see the Broadcast panel's
 * per-channel applicant counts).
 */

/**
 * The candidate-facing URL for a job: the company's branded careers
 * subdomain, e.g. https://acme.searchjob.ai/jobs/<jobId>.
 *
 *   • Production: prepend the company slug as a subdomain on the apex domain
 *     (last two labels), so it works whether the employer is on the apex or
 *     already on a subdomain.
 *   • Localhost / IP: browsers have no real subdomains there, so use the
 *     `?subdomain=<slug>` override that getSubdomainFromLocation honours.
 *   • No slug: plain `/jobs/<jobId>` deep link.
 */
export function buildPublicJobUrl({ jobId, companySlug } = {}) {
  if (typeof window === 'undefined' || !jobId) return '';
  const { protocol, hostname, origin } = window.location;

  if (!companySlug) return `${origin}/jobs/${jobId}`;

  const isLocal =
    hostname === 'localhost' ||
    hostname.endsWith('.localhost') ||
    /^\d+\.\d+\.\d+\.\d+$/.test(hostname);
  if (isLocal) {
    return `${origin}/jobs/${jobId}?subdomain=${companySlug}`;
  }

  const labels = hostname.split('.');
  const apex = labels.length > 2 ? labels.slice(-2).join('.') : hostname;
  return `${protocol}//${companySlug}.${apex}/jobs/${jobId}`;
}

/** Add (or replace) the `src` tag on a job URL. */
export function withSource(url, src) {
  if (!url || !src) return url || '';
  try {
    const u = new URL(url);
    u.searchParams.set('src', src);
    return u.toString();
  } catch (e) {
    return `${url}${url.includes('?') ? '&' : '?'}src=${encodeURIComponent(src)}`;
  }
}

/**
 * Clipboard write. Tries the async Clipboard API, then falls back to a
 * hidden textarea — for non-secure contexts and for browsers that refuse the
 * API (permissions policy, unfocused document). Throws only if both fail.
 */
export async function copyText(text) {
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch (e) {
      /* fall through to the textarea method */
    }
  }
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.setAttribute('readonly', '');
  ta.style.position = 'fixed';
  ta.style.opacity = '0';
  // Inside an open dialog the focus trap pulls focus back from anything
  // outside it, so the textarea has to live inside the dialog to be selected.
  const host = document.activeElement?.closest?.('[role="dialog"]') || document.body;
  host.appendChild(ta);
  ta.select();
  let ok = false;
  try {
    ok = document.execCommand('copy');
  } finally {
    host.removeChild(ta);
  }
  if (!ok) throw new Error('Copy failed');
}

const metaLines = (job) =>
  [
    job.companyName && `Company: ${job.companyName}`,
    job.location && `Location: ${job.location}`,
    job.jobType && `Type: ${job.jobType}`,
    job.experience && `Experience: ${job.experience}`,
  ].filter(Boolean);

/** Long-form message for WhatsApp, Telegram, email and "copy message". */
export function buildShareMessage(job, url) {
  return [
    'Hi,',
    '',
    'We have an opening that might interest you:',
    '',
    `We're hiring: ${job.title || 'this role'}`,
    ...metaLines(job),
    '',
    'View the full job description and apply here:',
    url,
    '',
    "Know someone who'd be a great fit? Feel free to forward this.",
  ].join('\n');
}

/** X caps posts at 280 characters, so it gets a compact variant. */
export function buildTweet(job, url) {
  const company = job.companyName ? ` at ${job.companyName}` : '';
  const meta = [job.location, job.jobType].filter(Boolean).join(' · ');
  return [`We're hiring: ${job.title || 'this role'}${company}`, meta || null, `Apply: ${url}`, '#hiring #jobs']
    .filter((l) => l !== null)
    .join('\n');
}

/** LinkedIn post text — copied to the clipboard (LinkedIn can't pre-fill it). */
export function buildLinkedInPost(job, url) {
  const company = job.companyName ? ` at ${job.companyName}` : '';
  return [
    `We are hiring: ${job.title || 'this role'}${company}.`,
    '',
    ...metaLines(job).filter((l) => !l.startsWith('Company:')),
    '',
    'If you or someone in your network is a good fit, view the full job description and apply here:',
    url,
    '',
    '#Hiring #JobOpening',
  ].join('\n');
}

const openWindow = (url) => window.open(url, '_blank', 'noopener,noreferrer');

/**
 * Share a job on one platform with a link tagged for that platform.
 *
 * Returns { copied } — true when text was placed on the clipboard so the
 * caller can tell the user to paste it (LinkedIn, copy link).
 */
export async function shareJob(platform, job, baseUrl) {
  const url = withSource(baseUrl, platform);
  const enc = encodeURIComponent;

  switch (platform) {
    case 'whatsapp':
      openWindow(`https://wa.me/?text=${enc(buildShareMessage(job, url))}`);
      return { copied: false };

    case 'telegram':
      openWindow(`https://t.me/share/url?url=${enc(url)}&text=${enc(buildShareMessage(job, url))}`);
      return { copied: false };

    case 'x':
      openWindow(`https://twitter.com/intent/tweet?text=${enc(buildTweet(job, url))}`);
      return { copied: false };

    case 'email': {
      const subject = job.title
        ? `Job opening: ${job.title}${job.companyName ? ` at ${job.companyName}` : ''}`
        : 'Job opening';
      window.location.href = `mailto:?subject=${enc(subject)}&body=${enc(buildShareMessage(job, url))}`;
      return { copied: false };
    }

    case 'linkedin': {
      // LinkedIn's share box can't be pre-filled with text, so the post is
      // copied for the user to paste and the box opens with the link preview.
      let copied = true;
      try {
        await copyText(buildLinkedInPost(job, url));
      } catch (e) {
        copied = false;
      }
      openWindow(`https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}`);
      return { copied };
    }

    case 'copy_link':
      await copyText(url);
      return { copied: true };

    default:
      return { copied: false };
  }
}
